/**
 * BlinterestSettings — ingest controls for the Admin Blinterest tab.
 * Owns nothing; every state + handler is passed in from the parent.
 * Also embeds the homepage-rails toggle card as a companion setting.
 */
import { Play, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AdminBlinterestHomepageControlsCard from "@/components/AdminBlinterestHomepageControlsCard";

export default function BlinterestSettings({
  status,
  ingestLimit,
  onIngestLimitChange,
  crawlLimit,
  onCrawlLimitChange,
  crawlPerBiz,
  onCrawlPerBizChange,
  onKickIngest,
  onKickDeepCrawl,
  onRefreshStatus,
}) {
  return (
    <>
      <AdminBlinterestHomepageControlsCard />
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg text-tbc-navy flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-tbc-gold" />
            Content Seeding
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <p className="font-heading text-sm text-tbc-navy">Homepage Ingest</p>
              <p className="text-xs text-gray-500">
                Scrape OG image + title from each business homepage. Idempotent —
                skips businesses that already have pins.
              </p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={ingestLimit}
                  onChange={(e) => onIngestLimitChange(parseInt(e.target.value || "50", 10))}
                  className="h-9 w-24"
                  data-testid="ingest-limit-input"
                />
                <Button
                  onClick={onKickIngest}
                  disabled={status.running}
                  size="sm"
                  className="bg-tbc-navy text-white rounded-full"
                  data-testid="run-homepage-ingest-btn"
                >
                  <Play className="w-3 h-3 mr-1" />
                  {status.running ? "Running…" : "Run"}
                </Button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <p className="font-heading text-sm text-tbc-navy">Sitemap Deep Crawl</p>
              <p className="text-xs text-gray-500">
                Walk each business&apos;s sitemap and harvest product / blog / gallery
                pages. Perfect for filling out the feed.
              </p>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Businesses</span>
                  <Input
                    type="number"
                    value={crawlLimit}
                    onChange={(e) => onCrawlLimitChange(parseInt(e.target.value || "20", 10))}
                    className="h-9 w-20"
                    data-testid="crawl-limit-input"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">× pins</span>
                  <Input
                    type="number"
                    value={crawlPerBiz}
                    onChange={(e) => onCrawlPerBizChange(parseInt(e.target.value || "8", 10))}
                    className="h-9 w-16"
                    data-testid="crawl-per-biz-input"
                  />
                </div>
                <Button
                  onClick={onKickDeepCrawl}
                  disabled={status.running}
                  size="sm"
                  className="bg-tbc-gold text-tbc-navy rounded-full"
                  data-testid="run-deep-crawl-btn"
                >
                  <Play className="w-3 h-3 mr-1" />
                  {status.running ? "Running…" : "Run"}
                </Button>
              </div>
            </div>
          </div>

          {status.started_at && (
            <div className="text-xs text-gray-600 bg-tbc-cream rounded-lg p-3 flex flex-wrap gap-4">
              <span>
                Status:{" "}
                <strong className={status.running ? "text-tbc-navy" : "text-green-700"}>
                  {status.running ? "running" : "idle"}
                </strong>
              </span>
              <span>Attempted: {status.attempted || 0}</span>
              <span className="text-green-700">Created: {status.created || 0}</span>
              <span className="text-gray-500">Skipped: {status.skipped || 0}</span>
              <span className="text-red-600">Failed: {status.failed || 0}</span>
              <button
                onClick={onRefreshStatus}
                type="button"
                className="ml-auto inline-flex items-center gap-1 text-tbc-navy hover:underline"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
